import { useEffect, useMemo, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ProposalSaveDataType, LeadProposalFullType } from '@econtractor/types';
import * as apiProposal from 'api/apiProposal';
import { useToast } from 'context/ToastContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import ESpinner from 'components/others/Spinner';
import { useTemplateProductsMap } from '../share/model/useTemplateProductsMap';
import { buildDefaultDocumentMeta } from '../share/model/buildDefaultDocumentMeta';
import ProposalEditorForm from './ProposalEditorForm';
import type { ProposalEditFormValues } from './types';

interface ProposalEditorOffcanvasProps {
    mode: 'dir' | 'lead';
    proposalId: number | null;
    leadId?: number | string;
    leadEmail?: string;
    leadName?: string;
    leadStatus?: number;
    /** Lead/client record for template key substitution. */
    objectData?: unknown;
    onClose: () => void;
    onSaved: () => Promise<void> | void;
    onGenerated?: () => Promise<void> | void;
}

const createEmptyData = (usdDefault: boolean, vatDefault: boolean): ProposalEditFormValues => ({
    proposalType: 'proposal',
    proposalName: '',
    proposalComment: '',
    proposalNumber: '',
    proposalDt: null,
    proposalLogo: '',
    proposalBackground: '',
    proposalTitle: '',
    proposalIntro: '',
    proposalOutro: '',
    pricesInUsd: Boolean(usdDefault),
    proposalVat: Boolean(vatDefault),
    proposalTitlePage: false,
    products: [],
    additionalItems: [],
});

const ProposalEditorOffcanvas = ({
    mode,
    proposalId,
    leadId,
    leadEmail = '',
    leadName = '',
    leadStatus,
    objectData,
    onClose,
    onSaved,
    onGenerated,
}: ProposalEditorOffcanvasProps) => {
    const editorProposalType = 'proposal' as const;

    const { t } = useTranslation(['Proposal', 'common']);
    const { showToast } = useToast();
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const { productsMap, loading: loadingProducts } = useTemplateProductsMap();
    const rate = Number(localSettings?.RATE?.current || 1) || 1;
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const emptyData = useMemo(
        () => createEmptyData(params.usdDefault, params.vatDefault),
        [params.usdDefault, params.vatDefault],
    );
    const [formData, setFormData] = useState<ProposalEditFormValues>(() =>
        createEmptyData(params.usdDefault, params.vatDefault),
    );
    const sourceProposalId = proposalId != null && proposalId < 0 ? Math.abs(proposalId) : null;
    const isCreate = proposalId === 0 || sourceProposalId != null;

    const title = useMemo(() => {
        if (mode === 'lead') return t('lead.editTitle');
        return isCreate ? t('edit.titleCreate') : t('edit.titleUpdate');
    }, [isCreate, mode, t]);

    const toPayload = (payload: ProposalEditFormValues): ProposalSaveDataType => {
        const base: ProposalSaveDataType = {
            proposalType: payload.proposalType,
            proposalName: payload.proposalName,
            proposalComment: payload.proposalComment,
            proposalLogo: payload.proposalLogo,
            proposalBackground: payload.proposalBackground,
            proposalTitle: payload.proposalTitle,
            proposalIntro: payload.proposalIntro,
            proposalOutro: payload.proposalOutro,
            pricesInUsd: payload.pricesInUsd,
            proposalVat: payload.proposalVat,
            proposalTitlePage: Boolean(payload.proposalTitlePage),
            products: payload.products,
            additionalItems: payload.additionalItems,
        };
        // Document number/date exist only on lead proposals (not directory templates).
        if (mode === 'lead') {
            base.proposalNumber = String(payload.proposalNumber || '').trim() || null;
            base.proposalDt = payload.proposalDt && payload.proposalDt > 0 ? payload.proposalDt : null;
        }
        return base;
    };

    const withDuplicateName = (name: string): string => {
        const baseName = String(name || '').trim();
        const suffix = t('card.duplicateNameSuffix');
        if (!baseName) return suffix.trim();
        if (baseName.endsWith(suffix.trim())) return baseName;
        return `${baseName}${suffix}`;
    };

    const resolveLeadDocumentMeta = async (): Promise<Pick<ProposalEditFormValues, 'proposalNumber' | 'proposalDt'>> => {
        const targetLeadId = Number(leadId || 0);
        if (!targetLeadId) {
            return { proposalNumber: '', proposalDt: null };
        }
        const rows = await apiProposal.getLeadProposalGenerations(targetLeadId);
        const existingCount = (rows || []).filter((row) => !Number(row.deleted || 0)).length;
        return buildDefaultDocumentMeta({
            entityId: targetLeadId,
            sequence: existingCount + 1,
            letter: t('edit.fields.documentNumberLetterLead'),
        });
    };

    const fetchData = async () => {
        if (proposalId === null) return;
        if (proposalId === 0) {
            setFormData(emptyData);
            return;
        }

        const loadId = sourceProposalId || proposalId;
        setLoadingData(true);
        try {
            const data =
                mode === 'lead' && leadId
                    ? await apiProposal.getLeadProposal(leadId)
                    : await apiProposal.getDirProposal(loadId);

            if (!data) {
                setFormData(emptyData);
                return;
            }

            const leadProposal = mode === 'lead' ? (data as LeadProposalFullType) : null;
            const proposalName = String(data.proposalName || (mode === 'lead' ? leadName : '') || '');
            let proposalNumber = String(leadProposal?.proposalNumber || '');
            let proposalDt =
                leadProposal?.proposalDt && Number(leadProposal.proposalDt) > 0
                    ? Number(leadProposal.proposalDt)
                    : null;

            // New / empty lead КП — auto number `{leadId}-{seq}Л` and today's date.
            if (mode === 'lead' && leadId && (!proposalNumber.trim() || !proposalDt)) {
                const meta = await resolveLeadDocumentMeta();
                const nextNumber = proposalNumber.trim() || meta.proposalNumber;
                const nextDt = proposalDt || meta.proposalDt;
                proposalNumber = nextNumber;
                proposalDt = nextDt;

                // Persist so reopen / regenerate keeps the same number.
                if (proposalId > 0) {
                    await apiProposal.saveLeadProposal(leadId, proposalId, {
                        proposalType: editorProposalType,
                        proposalName: sourceProposalId ? withDuplicateName(proposalName) : proposalName,
                        proposalComment: String(data.proposalComment || ''),
                        proposalLogo: String(data.proposalLogo || ''),
                        proposalBackground: String(data.proposalBackground || ''),
                        proposalTitle: String(data.proposalTitle || ''),
                        proposalIntro: String(data.proposalIntro || ''),
                        proposalOutro: String(data.proposalOutro || ''),
                        pricesInUsd: Boolean(data.pricesInUsd),
                        proposalVat: Boolean(data.proposalVat),
                        proposalTitlePage: Boolean(data.proposalTitlePage),
                        products: Array.isArray(data.products) ? data.products : [],
                        additionalItems: Array.isArray(data.additionalItems) ? data.additionalItems : [],
                        proposalNumber: nextNumber || null,
                        proposalDt: nextDt,
                    });
                }
            }

            setFormData({
                proposalType: editorProposalType,
                proposalName: sourceProposalId ? withDuplicateName(proposalName) : proposalName,
                proposalComment: String(data.proposalComment || ''),
                proposalNumber,
                proposalDt,
                proposalLogo: String(data.proposalLogo || ''),
                proposalBackground: String(data.proposalBackground || ''),
                proposalTitle: String(data.proposalTitle || ''),
                proposalIntro: String(data.proposalIntro || ''),
                proposalOutro: String(data.proposalOutro || ''),
                pricesInUsd: Boolean(data.pricesInUsd),
                proposalVat: Boolean(data.proposalVat),
                proposalTitlePage: Boolean(data.proposalTitlePage),
                products: Array.isArray(data.products) ? data.products : [],
                additionalItems: Array.isArray(data.additionalItems) ? data.additionalItems : [],
            });
        } finally {
            setLoadingData(false);
        }
    };

    const handleSubmit = async (payload: ProposalEditFormValues) => {
        if (proposalId === null) return;
        setLoading(true);

        let stat = false;
        if (mode === 'lead') {
            if (!leadId || !proposalId) stat = false;
            else stat = await apiProposal.saveLeadProposal(leadId, proposalId, toPayload(payload));
        } else if (isCreate) {
            const createdId = await apiProposal.createDirProposal(toPayload(payload));
            stat = Boolean(createdId);
        } else {
            stat = await apiProposal.saveDirProposal(proposalId, toPayload(payload));
        }
        setLoading(false);

        if (!stat) {
            showToast(t('common:messError'), 'danger');
            return;
        }

        if (isCreate && mode === 'dir') {
            showToast(t('messCreated'), 'success');
            await onSaved();
            onClose();
            return;
        }

        showToast(t('messSaved'), 'success');
        await onSaved();
    };

    useEffect(() => {
        if (proposalId === null) return;
        void fetchData();
    }, [proposalId, mode, leadId, leadName]);

    return (
        <Offcanvas show={proposalId !== null} onHide={onClose} placement="end" className="off-import">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{title}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="proposal-editor-offcanvas-body position-relative pb-0 d-flex flex-column px-0">
                {loadingData || loadingProducts ? (
                    <ESpinner />
                ) : (
                    <ProposalEditorForm
                        key={`${mode}-${proposalId || 0}`}
                        mode={mode}
                        leadId={leadId}
                        leadProposalId={proposalId && proposalId > 0 ? proposalId : undefined}
                        leadEmail={leadEmail}
                        leadName={leadName}
                        leadStatus={leadStatus}
                        objectData={objectData}
                        initialValues={formData}
                        loading={loading}
                        submitLabel={isCreate ? t('common:buttons.create') : t('common:buttons.save')}
                        productsMap={productsMap}
                        rate={rate}
                        onSubmit={handleSubmit}
                        onGenerated={onGenerated}
                    />
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default ProposalEditorOffcanvas;
